import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import TopWorst from "./topWorst";

function ViewPerformersTables(props: any) {
  let analysis: any = props.summary.analysis;
  let titles = props.titles;
  let view = props.view;
  let performersType: "pl" | "price move" = props.performersType;
  let period: string = props.period || "day";
  let category: string = props.category || "all";

  let periodKey = period;

  // Pick the full-book lists or the selected category's lists
  let source: any = category != "all" && analysis.topWorstPerformaners.byCategory ? analysis.topWorstPerformaners.byCategory[category] || analysis.topWorstPerformaners : analysis.topWorstPerformaners;

  let listsByPeriod: any = {
    day: { worst: source.worst5Day, top: source.top5Day },
    "3day": { worst: source.worst5ThreeDay, top: source.top5ThreeDay },
    mtd: { worst: source.worst5MTD, top: source.top5MTD },
    ytd: { worst: source.worst5YTD, top: source.top5YTD },
  };

  let worstList = (listsByPeriod[period] || listsByPeriod.day).worst || [];
  let topList = (listsByPeriod[period] || listsByPeriod.day).top || [];

  let sortObject: any = {
    pl: { day: "groupDayPl", "3day": "groupDayPl", mtd: "groupMTDPl", ytd: "groupYTDPl" },
    "price move": { day: "groupDayPriceMoveSum", "3day": "groupThreeDayPriceMove", mtd: "groupMTDPriceMoveSum", ytd: "groupYTDPriceMoveSum" },
  };

  let metricByPeriod: any = {
    pl: { day: "Day P&L (USD)", "3day": "3-Day P&L (USD)", mtd: "MTD P&L (USD)", ytd: "YTD P&L (USD)" },
    "price move": { day: "Day Price Move", "3day": "3-Day Price Move", mtd: "MTD Price Move", ytd: "YTD Price Move" },
  };

  let periodLabel: any = { day: "Previous Day", "3day": "3 Days", mtd: "MTD", ytd: "YTD" };

  let sortField = sortObject[performersType][periodKey] || sortObject[performersType].day;
  let metricTitle = metricByPeriod[performersType][periodKey] || metricByPeriod[performersType].day;
  let label = periodLabel[period] || "Previous Day";
  let categoryLabel = category != "all" ? category + " " : "";

  // Curated XLSX export: one tab per category. Each tab stacks every period
  // (Previous Day, 3 Days, MTD, YTD) with its own Worst 10 / Top 10, each ranked
  // by that period's basis-point price move. Columns reorder dynamically per
  // period so the active period's move/P&L sits right beside Mid.
  function downloadCSV() {
    let periodMetric: any = { day: "Day Price Move", "3day": "3-Day Price Move", mtd: "MTD Price Move", ytd: "YTD Price Move" };
    let periodsList: any = [{ key: "day", name: "Previous Day" }, { key: "3day", name: "3 Days" }, { key: "mtd", name: "MTD" }, { key: "ytd", name: "YTD" }];

    // Same column curation/ordering as the on-screen page, parameterized by
    // period: the period's move/P&L columns are pulled to the front of the move
    // block (right beside Mid) so the active period is most prominent.
    function orderedTitles(per: string) {
      let midTitle = titles.find((title: any) => title.startsWith("Mid "));
      let prevMidTitle = titles.find((title: any) => title.startsWith("Yesterday's Mid"));
      let moveBlock = ["Day Price Move", "Day P&L (USD)", "3-Day Price Move", "3-Day P&L (USD)", "MTD Price Move", "MTD P&L (USD)", "YTD Price Move", "YTD P&L (USD)"];
      let periodFirst: any = { day: ["Day Price Move", "Day P&L (USD)"], "3day": ["3-Day Price Move", "3-Day P&L (USD)"], mtd: ["MTD Price Move", "MTD P&L (USD)"], ytd: ["YTD Price Move", "YTD P&L (USD)"] };
      let front = periodFirst[per] || [];
      let orderedMoveBlock = front.concat(moveBlock.filter((title: any) => !front.includes(title)));
      let order = ["L/S", "Strategy", "Issuer", "BB Ticker", "Notional Amount", "USD Market Value", "Bid", "Ask", midTitle, prevMidTitle, ...orderedMoveBlock, "MTD Int. (USD)", "YTD Int. (USD)"];
      return order.filter((title: any) => title && titles.includes(title));
    }

    function buildHeader(cols: any) {
      return cols.map((title: any) => (title.startsWith("Mid ") ? "Mid" : title.startsWith("Yesterday's Mid") ? "Previous Mid" : title));
    }

    function getLists(bucket: string, per: string) {
      let src: any = bucket == "All" ? analysis.topWorstPerformaners : (analysis.topWorstPerformaners.byCategory && analysis.topWorstPerformaners.byCategory[bucket]) || analysis.topWorstPerformaners;
      let byP: any = {
        day: { worst: src.worst5Day, top: src.top5Day },
        "3day": { worst: src.worst5ThreeDay, top: src.top5ThreeDay },
        mtd: { worst: src.worst5MTD, top: src.top5MTD },
        ytd: { worst: src.worst5YTD, top: src.top5YTD },
      };
      return byP[per] || byP.day;
    }

    // Match the on-screen ranking: flatten legs, sort by the period's bps price
    // move (desc for top, asc for worst), cap to 10. Returns data rows only.
    function dataRows(lists: any, direction: string, metric: string, cols: any) {
      let flat: any = [];
      (lists || []).forEach((group: any) => (group.data || []).forEach((position: any) => flat.push(position)));
      flat.sort((a: any, b: any) => {
        let av = parseFloat(a[metric]) || 0;
        let bv = parseFloat(b[metric]) || 0;
        return direction == "desc" ? bv - av : av - bv;
      });
      return flat.slice(0, 10).map((position: any) => cols.map((title: any) => position[title]));
    }

    // One tab per category; inside, all periods stacked with Worst 10 then Top 10
    let wb = XLSX.utils.book_new();
    let buckets = ["All", "IG", "HY", "CDS", "Non USD", "Rates"];
    for (let i = 0; i < buckets.length; i++) {
      let bucket = buckets[i];
      let sheet: any = [];
      for (let j = 0; j < periodsList.length; j++) {
        let per = periodsList[j];
        let lists = getLists(bucket, per.key);
        let metric = periodMetric[per.key];
        let cols = orderedTitles(per.key);
        let header = buildHeader(cols);
        sheet.push([`WORST 10 - ${bucket} - ${per.name}`]);
        sheet.push(header);
        dataRows(lists.worst, "asc", metric, cols).forEach((row: any) => sheet.push(row));
        sheet.push([]);
        sheet.push([`TOP 10 - ${bucket} - ${per.name}`]);
        sheet.push(header);
        dataRows(lists.top, "desc", metric, cols).forEach((row: any) => sheet.push(row));
        sheet.push([]);
        sheet.push([]);
      }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet), bucket.slice(0, 31));
    }
    XLSX.writeFile(wb, `performers_${props.date}.xlsx`);
  }

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="unassigned-container" id="content">
      <button id="download-btn" onClick={downloadCSV} className="btn upload-btn" style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 1000 }}>
        Download CSV
      </button>
      <p
        className="title-unassigned margin-bottom-5 clickable"
        style={{
          cursor: "pointer",
        }}
        onClick={(event) => props.reRouteSummary(event, sortField, -1, metricTitle)}
      >
        Worst 10 {label} {categoryLabel}Performers
      </p>

      <TopWorst titles={titles} topWorst={worstList} sortTitle={metricTitle} direction={"asc"} getTrades={props.getTrades} positionInfo={props.positionInfo} view={view} screenWidth={screenWidth} date={props.date} idPrefix={"1"} />

      <p
        className="title-unassigned margin-bottom-5 clickable"
        style={{
          cursor: "pointer",
        }}
        onClick={(event) => props.reRouteSummary(event, sortField, 1, metricTitle)}
      >
        Top 10 {label} {categoryLabel}Performers
      </p>
      <TopWorst titles={titles} topWorst={topList} sortTitle={metricTitle} direction={"desc"} getTrades={props.getTrades} positionInfo={props.positionInfo} view={view} screenWidth={screenWidth} date={props.date} idPrefix={"3"} />
    </div>
  );
}

export default ViewPerformersTables;
